/**
 * DPL DEW Coupon Management
 * Production-grade coupon code generator and management system
 */

'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Ticket,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  TrendingUp,
  Users,
  Calendar,
  RefreshCw,
  Download,
  Eye,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { adminApi } from '@/admin/services/api';
import { adminAuth } from '@/admin/lib/auth';
import type { 
  PaginatedResponse,
  ApiResponse 
} from '@/admin/types';

interface CouponCode {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  max_uses?: number;
  used_count: number;
  expires_at?: string;
  applies_to_plan?: string[];
  is_active: boolean;
  created_at: string;
  created_by: string;
  analytics?: {
    total_revenue: number;
    total_savings: number;
    conversion_rate: number;
    usage_by_plan: Record<string, number>;
  };
}

const DISCOUNT_TYPE_COLORS = {
  percentage: 'bg-emerald-600/20 text-emerald-400 border-emerald-600/30',
  fixed: 'bg-blue-600/20 text-blue-400 border-blue-600/30'
};

const STATUS_COLORS = {
  active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  inactive: 'bg-slate-600/20 text-muted-foreground border-slate-600/30',
  expired: 'bg-red-500/20 text-red-400 border-red-500/30'
};

export function CouponManagement() {
  const [coupons, setCoupons] = useState<CouponCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    is_active: undefined as boolean | undefined
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    total_pages: 0
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<CouponCode | null>(null);
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<CouponCode | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: 10,
    max_uses: 100,
    expires_at: '',
    applies_to_plan: [] as string[],
    is_active: true
  });

  const loadCoupons = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      const response = await adminApi.getCouponCodes(params);
      
      if (response.success && response.data) {
        setCoupons(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const generateCouponCode = () => {
    const length = 8;
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCoupon(prev => ({ ...prev, code: result }));
  };

  const createCoupon = async () => {
    try {
      setActionLoading('create');
      
      const response = await adminApi.createCouponCode(newCoupon);
      
      if (response.success) {
        setCreateDialogOpen(false);
        setNewCoupon({
          code: '',
          discount_type: 'percentage',
          discount_value: 10,
          max_uses: 100,
          expires_at: '',
          applies_to_plan: [],
          is_active: true
        });
        await loadCoupons();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create coupon');
    } finally {
      setActionLoading(null);
    }
  };

  const updateCoupon = async (couponId: string, data: Partial<CouponCode>) => {
    try {
      setActionLoading(`update-${couponId}`);
      
      const response = await adminApi.updateCouponCode(couponId, data);
      
      if (response.success) {
        setEditingCoupon(null);
        await loadCoupons();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update coupon');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteCoupon = async (couponId: string) => {
    if (!confirm('Are you sure you want to delete this coupon? This action cannot be undone.')) {
      return;
    }
    
    try {
      setActionLoading(`delete-${couponId}`);
      
      const response = await adminApi.deleteCouponCode(couponId);
      
      if (response.success) {
        await loadCoupons();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete coupon');
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const loadCouponAnalytics = async (couponId: string) => {
    try {
      const response = await adminApi.getCouponAnalytics(couponId);
      
      if (response.success && response.data) {
        setSelectedCoupon(prev => prev ? { ...prev, analytics: response.data } : null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    }
  };

  const handleFilterChange = (key: string, value: unknown) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', is_active: undefined });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const filteredCoupons = coupons.filter(coupon => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return coupon.code.toLowerCase().includes(searchLower);
    }
    if (filters.is_active !== undefined) {
      return coupon.is_active === filters.is_active;
    }
    return true;
  });

  useEffect(() => {
    loadCoupons();
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    if (selectedCoupon) {
      loadCouponAnalytics(selectedCoupon.id);
    }
  }, [selectedCoupon]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const isExpired = (expiresAt?: string) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const canManageCoupons = adminAuth.hasRole('billing_admin') || adminAuth.hasRole('super_admin');

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-medium text-foreground">Coupon Management</h1>
              <p className="text-sm text-muted-foreground">Create and manage discount codes</p>
            </div>
            <div className="flex items-center gap-4">
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    disabled={!canManageCoupons}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Coupon
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700 text-foreground">
                  <DialogHeader>
                    <DialogTitle>
                      {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Coupon Code</Label>
                        <div className="flex gap-2">
                          <Input
                            value={newCoupon.code}
                            onChange={(e) => setNewCoupon(prev => ({ ...prev, code: e.target.value }))}
                            placeholder="AUTO-GENERATED"
                            className="bg-slate-800/50 border-slate-700 text-foreground"
                          />
                          <Button
                            onClick={generateCouponCode}
                            variant="outline"
                            size="sm"
                            className="border-slate-600 text-foreground hover:bg-slate-800"
                          >
                            Generate
                          </Button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-slate-300">Discount Type</Label>
                        <Select
                          value={newCoupon.discount_type}
                          onValueChange={(value) => setNewCoupon(prev => ({ ...prev, discount_type: value as 'percentage' | 'fixed' }))}
                        >
                          <SelectTrigger className="bg-slate-800/50 border-slate-700 text-foreground">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="percentage">Percentage</SelectItem>
                            <SelectItem value="fixed">Fixed Amount</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">
                          {newCoupon.discount_type === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                        </Label>
                        <Input
                          type="number"
                          value={newCoupon.discount_value}
                          onChange={(e) => setNewCoupon(prev => ({ ...prev, discount_value: Number(e.target.value) }))}
                          placeholder={newCoupon.discount_type === 'percentage' ? '10' : '10.00'}
                          className="bg-slate-800/50 border-slate-700 text-foreground"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-slate-300">Max Uses</Label>
                        <Input
                          type="number"
                          value={newCoupon.max_uses}
                          onChange={(e) => setNewCoupon(prev => ({ ...prev, max_uses: Number(e.target.value) }))}
                          placeholder="100"
                          className="bg-slate-800/50 border-slate-700 text-foreground"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Expires At</Label>
                      <Input
                        type="datetime-local"
                        value={newCoupon.expires_at}
                        onChange={(e) => setNewCoupon(prev => ({ ...prev, expires_at: e.target.value }))}
                        className="bg-slate-800/50 border-slate-700 text-foreground"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <Button
                        onClick={createCoupon}
                        disabled={actionLoading === 'create' || !newCoupon.code}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"
                      >
                        {actionLoading === 'create' ? 'Creating...' : 'Create Coupon'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setCreateDialogOpen(false)}
                        className="border-slate-600 text-foreground hover:bg-slate-800"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                onClick={() => loadCoupons()}
                disabled={loading}
                variant="outline"
                size="sm"
                className="border-slate-700 text-foreground hover:bg-slate-800"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-800 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <Ticket className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Coupons</p>
                  <p className="text-2xl font-medium text-foreground">{coupons.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Coupons</p>
                  <p className="text-2xl font-medium text-foreground">
                    {coupons.filter(c => c.is_active && !isExpired(c.expires_at)).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <Users className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Uses</p>
                  <p className="text-2xl font-medium text-foreground">
                    {coupons.reduce((sum, c) => sum + c.used_count, 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-800 bg-slate-900/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-slate-800/50">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                  <p className="text-2xl font-medium text-foreground">
                    {coupons.length > 0 ? 
                      formatPercentage((coupons.reduce((sum, c) => sum + c.used_count, 0) / 
                      coupons.reduce((sum, c) => sum + (c.max_uses || 0), 0)) * 100) : '0.0%'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-800 bg-slate-900/50 mb-6">
          <CardContent className="p-6">
            <div className="flex gap-4 items-end">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search coupons..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 bg-slate-800/50 border-slate-700 text-foreground placeholder:text-muted-foreground"
                />
              </div>
              
              <Select
                value={filters.is_active === undefined ? '' : filters.is_active.toString()}
                onValueChange={(value) => handleFilterChange('is_active', value === '' ? undefined : value === 'true')}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-foreground">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <Button
                onClick={clearFilters}
                variant="outline"
                size="sm"
                className="border-slate-700 text-foreground hover:bg-slate-800"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Coupons Table */}
        <Card className="border-slate-800 bg-slate-900/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-800/30">
                  <TableRow>
                    <TableHead className="text-muted-foreground font-semibold">Code</TableHead>
                    <TableHead className="text-muted-foreground font-semibold">Type</TableHead>
                    <TableHead className="text-muted-foreground font-semibold">Value</TableHead>
                    <TableHead className="text-muted-foreground font-semibold">Usage</TableHead>
                    <TableHead className="text-muted-foreground font-semibold">Status</TableHead>
                    <TableHead className="text-muted-foreground font-semibold">Expires</TableHead>
                    <TableHead className="text-muted-foreground font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin text-emerald-500 mx-auto mb-2" />
                        <p className="text-muted-foreground">Loading coupons...</p>
                      </TableCell>
                    </TableRow>
                  ) : filteredCoupons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <Ticket className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">No coupons found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCoupons.map((coupon) => (
                      <TableRow key={coupon.id} className="border-slate-800 hover:bg-slate-800/30">
                        <TableCell className="py-4">
                          <div className="flex items-center gap-2">
                            <code className="bg-slate-800 px-2 py-1 rounded text-emerald-400 font-mono text-sm">
                              {coupon.code}
                            </code>
                            <Button
                              onClick={() => copyToClipboard(coupon.code)}
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={DISCOUNT_TYPE_COLORS[coupon.discount_type]}>
                            {coupon.discount_type.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-foreground font-medium">
                            {coupon.discount_type === 'percentage' ? 
                              `${coupon.discount_value}%` : 
                              formatCurrency(coupon.discount_value)
                            }
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-foreground">{coupon.used_count}</span>
                            {coupon.max_uses && (
                              <span className="text-muted-foreground">/ {coupon.max_uses}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            !coupon.is_active ? STATUS_COLORS.inactive :
                            isExpired(coupon.expires_at) ? STATUS_COLORS.expired :
                            STATUS_COLORS.active
                          }>
                            {!coupon.is_active ? 'INACTIVE' :
                             isExpired(coupon.expires_at) ? 'EXPIRED' :
                             'ACTIVE'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {coupon.expires_at ? 
                              new Date(coupon.expires_at).toLocaleDateString() : 
                              'No expiration'
                            }
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-slate-800 border-slate-700">
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedCoupon(coupon);
                                  setAnalyticsDialogOpen(true);
                                }}
                                className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Analytics
                              </DropdownMenuItem>
                              
                              {canManageCoupons && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEditingCoupon(coupon);
                                    setNewCoupon({
                                      code: coupon.code,
                                      discount_type: coupon.discount_type,
                                      discount_value: coupon.discount_value,
                                      max_uses: coupon.max_uses,
                                      expires_at: coupon.expires_at || '',
                                      applies_to_plan: coupon.applies_to_plan || [],
                                      is_active: coupon.is_active
                                    });
                                    setCreateDialogOpen(true);
                                  }}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              
                              {canManageCoupons && (
                                <DropdownMenuItem
                                  onClick={() => deleteCoupon(coupon.id)}
                                  disabled={actionLoading === `delete-${coupon.id}`}
                                  className="text-slate-300 hover:text-foreground hover:bg-slate-700"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-muted-foreground">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} coupons
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                variant="outline"
                size="sm"
                className="border-slate-700 text-foreground hover:bg-slate-800"
              >
                Previous
              </Button>
              <span className="text-muted-foreground">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <Button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.total_pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.total_pages}
                variant="outline"
                size="sm"
                className="border-slate-700 text-foreground hover:bg-slate-800"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onOpenChange={setAnalyticsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-foreground max-w-2xl">
          <DialogHeader>
            <DialogTitle>Coupon Analytics</DialogTitle>
          </DialogHeader>
          {selectedCoupon && (
            <div className="space-y-6 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-slate-700 bg-slate-800/50">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Total Revenue</div>
                    <div className="text-2xl font-medium text-emerald-400">
                      {selectedCoupon.analytics?.total_revenue ? 
                        formatCurrency(selectedCoupon.analytics.total_revenue) : 
                        '$0'
                      }
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-700 bg-slate-800/50">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Total Savings</div>
                    <div className="text-2xl font-medium text-blue-400">
                      {selectedCoupon.analytics?.total_savings ? 
                        formatCurrency(selectedCoupon.analytics.total_savings) : 
                        '$0'
                      }
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-slate-700 bg-slate-800/50">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Conversion Rate</div>
                    <div className="text-2xl font-medium text-emerald-400">
                      {selectedCoupon.analytics?.conversion_rate ? 
                        formatPercentage(selectedCoupon.analytics.conversion_rate) : 
                        '0.0%'
                      }
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-slate-700 bg-slate-800/50">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Usage Count</div>
                    <div className="text-2xl font-medium text-foreground">
                      {selectedCoupon.used_count}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {selectedCoupon.analytics?.usage_by_plan && (
                <Card className="border-slate-700 bg-slate-800/50">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-3">Usage by Plan</div>
                    <div className="space-y-2">
                      {Object.entries(selectedCoupon.analytics.usage_by_plan).map(([plan, count]) => (
                        <div key={plan} className="flex items-center justify-between">
                          <span className="text-foreground capitalize">{plan}</span>
                          <span className="text-emerald-400 font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
